import { useRef, useState } from 'react';
import { OTHER_MISSING_ITEM_MAX_LENGTH, STANDARD_ROOM_ITEMS } from '@room-verify/shared';
import { ApiError, api, type CompletionResult, type Inspection } from '../api';
import { Button, IconAlert, IconCheck, IconCheckCircle } from '../components';
import { errorMessage, type Copy } from '../i18n';

export function RoomItemsScreen({
  copy,
  token,
  inspection,
  onCompleted,
  onAuthExpired,
}: {
  copy: Copy;
  token: string;
  inspection: Inspection;
  onCompleted: (result: CompletionResult) => void;
  onAuthExpired: () => void;
}) {
  const [complete, setComplete] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [otherEnabled, setOtherEnabled] = useState(false);
  const [otherText, setOtherText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);

  const otherValue = otherEnabled ? otherText.trim() : '';
  const missingBranchValid = !complete && (selected.size > 0 || otherValue.length > 0);
  const canSubmit = complete || missingBranchValid;

  const toggleItem = (item: string) => {
    setError(null);
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(item)) next.delete(item);
      else next.add(item);
      return next;
    });
  };

  const submit = async () => {
    if (inFlight.current) return;
    if (!canSubmit) {
      setError(copy.missingItemsEmpty);
      return;
    }

    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      const { inspection: result } = await api.completeInspection(token, inspection.id, {
        complete,
        missingItems: complete ? [] : [...selected],
        otherMissingItem: complete ? null : otherValue || null,
      });
      onCompleted(result);
    } catch (caught) {
      if (caught instanceof ApiError && caught.isAuthExpiry) {
        onAuthExpired();
      } else {
        setError(errorMessage(copy, caught instanceof ApiError ? caught.code : 'NETWORK'));
      }
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <div className="screen">
      <div className="items-head">
        <h2 className="section-title">{copy.roomItems}</h2>
        <p className="items-room">
          {copy.roomNumber} <strong className="num">{inspection.roomNumber}</strong>
        </p>
      </div>

      <fieldset className="items-choice">
        <legend className="items-question">{copy.roomItemsQuestion}</legend>

        <label className={`choice-card${complete ? ' is-selected' : ''}`}>
          <input
            type="radio"
            name="items-state"
            checked={complete}
            onChange={() => {
              setComplete(true);
              setError(null);
            }}
          />
          <span className="choice-mark" aria-hidden="true">
            <IconCheck size={14} />
          </span>
          <span className="choice-body">
            <span className="choice-title">{copy.everythingComplete}</span>
            <span className="choice-hint">{copy.everythingCompleteHint}</span>
          </span>
        </label>

        <label className={`choice-card${!complete ? ' is-selected' : ''}`}>
          <input
            type="radio"
            name="items-state"
            checked={!complete}
            onChange={() => {
              setComplete(false);
              setError(null);
            }}
          />
          <span className="choice-mark" aria-hidden="true">
            <IconCheck size={14} />
          </span>
          <span className="choice-body">
            <span className="choice-title">{copy.missingItems}</span>
            <span className="choice-hint">{copy.missingItemsHint}</span>
          </span>
        </label>
      </fieldset>

      {!complete && (
        <section className="missing-section" aria-labelledby="missing-title">
          <h3 id="missing-title" className="missing-title">
            {copy.selectMissingItems}
          </h3>

          {/* Item names stay in English in every locale — they are the identifiers
              management reads on the report, not translatable UI copy. */}
          <div className="item-grid">
            {STANDARD_ROOM_ITEMS.map((item) => {
              const isSelected = selected.has(item);
              return (
                <label key={item} className={`item-chip${isSelected ? ' is-selected' : ''}`}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleItem(item)}
                  />
                  <span className="item-check" aria-hidden="true">
                    {isSelected && <IconCheck size={13} />}
                  </span>
                  <span className="item-name" lang="en" dir="ltr">
                    {item}
                  </span>
                </label>
              );
            })}

            <label className={`item-chip${otherEnabled ? ' is-selected' : ''}`}>
              <input
                type="checkbox"
                checked={otherEnabled}
                onChange={() => {
                  setOtherEnabled((value) => !value);
                  setError(null);
                }}
              />
              <span className="item-check" aria-hidden="true">
                {otherEnabled && <IconCheck size={13} />}
              </span>
              <span className="item-name">{copy.otherItem}</span>
            </label>
          </div>

          {otherEnabled && (
            <div className="field other-field">
              <label className="sr-only" htmlFor="other-item">
                {copy.otherItemPlaceholder}
              </label>
              <input
                id="other-item"
                className="input"
                value={otherText}
                onChange={(event) => {
                  setOtherText(event.target.value.slice(0, OTHER_MISSING_ITEM_MAX_LENGTH));
                  setError(null);
                }}
                placeholder={copy.otherItemPlaceholder}
                maxLength={OTHER_MISSING_ITEM_MAX_LENGTH}
                autoComplete="off"
              />
            </div>
          )}
        </section>
      )}

      {error && (
        <div className="notice notice-danger" role="alert">
          <IconAlert size={16} />
          <span>{error}</span>
        </div>
      )}

      <div className="sticky-actions">
        <Button
          variant="accent"
          size="lg"
          block
          onClick={submit}
          loading={busy}
          disabled={!canSubmit}
          icon={<IconCheckCircle size={18} />}
        >
          {busy ? copy.completing : copy.completeInspection}
        </Button>
      </div>
    </div>
  );
}
